/**
 * Jira Data Service — fetches real data from Jira MCP APIs.
 * Used by hooks to populate Dashboard, Sprint, Backlog, Kanban, etc.
 * Falls back gracefully on errors.
 */

import { runTool } from './toolRunner'
import { Issue, Sprint, KPI, Project, User } from '../data/mockData'

// ─── Types matching what components expect ────────────────────────────────────

export interface JiraProject {
  id: string
  key: string
  name: string
  avatarUrl?: string
  projectTypeKey?: string
}
export interface JiraSprint {
  id: number
  name: string
  state: 'active' | 'future' | 'closed'
  startDate?: string
  endDate?: string
  completeDate?: string
  goal?: string
}
export interface JiraIssue {
  key: string
  id: string
  summary: string
  status: string
  statusCategory: 'new' | 'indeterminate' | 'done'
  priority: string
  issuetype: string
  assignee: string
  assigneeAvatar?: string
  storyPoints?: number
  labels: string[]
  created: string
  updated: string
  duedate?: string
  resolutiondate?: string
  epicKey?: string
  epicName?: string
  description?: string
}
export interface JiraBoard {
  id: number
  name: string
  type: string
  projectKey?: string
}
export interface SprintSummary {
  sprint: JiraSprint
  issues: JiraIssue[]
  totalPoints: number
  completedPoints: number
  progress: number
  byStatus: Record<string, number>
}
export interface ProjectDashboard {
  project: JiraProject
  boards: JiraBoard[]
  activeSprint: SprintSummary | null
  recentIssues: JiraIssue[]
  backlog: JiraIssue[]
  kpis: {
    totalIssues: number
    openIssues: number
    inProgress: number
    done: number
    velocity: number
    blockers: number
  }
}

// ─── Adapters: Jira → mockData format ─────────────────────────────────────────

function mapStatus(status: string): Issue['status'] {
  const s = status.toLowerCase()
  if (s.includes('done') || s.includes('closed') || s.includes('resolved'))
    return 'Done'
  if (s.includes('progress')) return 'In Progress'
  if (s.includes('review') || s.includes('testing') || s.includes('qa'))
    return 'Review'
  if (s.includes('backlog')) return 'Backlog'
  return 'To Do'
}
function mapPriority(priority: string): Issue['priority'] {
  const p = priority.toLowerCase()
  if (
    p.includes('high') ||
    p.includes('highest') ||
    p.includes('blocker') ||
    p.includes('critical')
  )
    return 'High'
  if (p.includes('low') || p.includes('lowest') || p.includes('trivial'))
    return 'Low'
  return 'Medium'
}
function mapIssueType(type: string): Issue['type'] {
  const t = type.toLowerCase()
  if (t.includes('bug')) return 'Bug'
  if (t.includes('story') || t.includes('historia')) return 'Story'
  if (t.includes('epic') || t.includes('épica')) return 'Epic'
  return 'Task'
}
function makeUser(name: string): User {
  const parts = name.split(' ')
  const initials =
    parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : name.slice(0, 2).toUpperCase()
  return {
    id: name.toLowerCase().replace(/\s/g, '-'),
    name,
    role: '',
    avatar: '',
    initials,
    workload: 0,
  }
}

/** Convert JiraIssue → mockData Issue */
export function toMockIssue(ji: JiraIssue): Issue {
  return {
    key: ji.key,
    summary: ji.summary,
    type: mapIssueType(ji.issuetype),
    status: mapStatus(ji.status),
    rawStatus: ji.status,
    // Preserve original Jira status for column matching
    priority: mapPriority(ji.priority),
    assignee:
      ji.assignee && ji.assignee !== 'Sin asignar'
        ? makeUser(ji.assignee)
        : undefined,
    points: ji.storyPoints || undefined,
    labels: ji.labels.length > 0 ? ji.labels : undefined,
    createdDate: ji.created ? ji.created.split('T')[0] : undefined,
  }
}

/** Convert JiraSprint → mockData Sprint */
export function toMockSprint(js: JiraSprint, issues: JiraIssue[]): Sprint {
  const totalPoints = issues.reduce((s, i) => s + (i.storyPoints || 0), 0)
  const completedPoints = issues
    .filter((i) => i.statusCategory === 'done')
    .reduce((s, i) => s + (i.storyPoints || 0), 0)
  const progress =
    totalPoints > 0 ? Math.round((completedPoints / totalPoints) * 100) : 0
  return {
    id: String(js.id),
    name: js.name,
    goal: js.goal || '',
    startDate: js.startDate ? js.startDate.split('T')[0] : '',
    endDate: js.endDate ? js.endDate.split('T')[0] : '',
    status:
      js.state === 'active'
        ? 'Active'
        : js.state === 'closed'
          ? 'Completed'
          : 'Planned',
    progress,
    totalPoints,
    completedPoints,
  }
}

/** Build KPIs from real Jira data */
export function buildKPIs(
  sprintIssues: JiraIssue[],
  backlogIssues: JiraIssue[],
  sprint: Sprint,
): KPI[] {
  const velocity = sprint.completedPoints
  const completion = sprint.progress
  const allIssues = [...sprintIssues, ...backlogIssues]
  const blockers = allIssues.filter(
    (i) =>
      i.priority.toLowerCase().includes('high') ||
      i.priority.toLowerCase().includes('highest') ||
      i.priority.toLowerCase().includes('blocker'),
  ).length
  const inProgress = sprintIssues.filter(
    (i) => mapStatus(i.status) === 'In Progress',
  ).length
  return [
    {
      id: 'kpi-velocity',
      label: 'Velocidad Sprint',
      value: `${velocity} pts`,
      trend: 'neutral' as const,
      trendValue: '—',
      description: 'Story points completados en el sprint',
    },
    {
      id: 'kpi-completion',
      label: 'Sprint Completion',
      value: `${completion}%`,
      trend: completion >= 70 ? ('up' as const) : ('down' as const),
      trendValue: completion >= 70 ? 'On track' : 'Behind',
      description: 'Progreso del sprint actual',
    },
    {
      id: 'kpi-wip',
      label: 'Work in Progress',
      value: inProgress,
      trend: inProgress > 5 ? ('down' as const) : ('neutral' as const),
      trendValue: inProgress > 5 ? 'Alto WIP' : 'Normal',
      description: 'Issues en progreso actualmente',
    },
    {
      id: 'kpi-blockers',
      label: 'Bloqueadores',
      value: blockers,
      trend: blockers > 0 ? ('down' as const) : ('up' as const),
      trendValue: blockers > 0 ? `${blockers} high priority` : 'Sin blockers',
      description: 'Issues de alta prioridad pendientes',
    },
  ]
}

/** Build a full Project object from Jira data */
export function buildMockProject(
  jiraProject: JiraProject,
  sprintSummary: SprintSummary | null,
  backlog: JiraIssue[],
  colorIndex: number = 0,
): Project {
  const colors = [
    {
      bg: 'bg-indigo-500',
      hex: '#6366f1',
    },
    {
      bg: 'bg-emerald-500',
      hex: '#10b981',
    },
    {
      bg: 'bg-amber-500',
      hex: '#f59e0b',
    },
    {
      bg: 'bg-rose-500',
      hex: '#f43f5e',
    },
    {
      bg: 'bg-violet-500',
      hex: '#8b5cf6',
    },
    {
      bg: 'bg-cyan-500',
      hex: '#06b6d4',
    },
  ]
  const color = colors[colorIndex % colors.length]
  const sprintIssues = sprintSummary?.issues || []
  const mockSprintIssues = sprintIssues.map(toMockIssue)
  const mockBacklog = backlog.map(toMockIssue)
  const sprint: Sprint = sprintSummary
    ? toMockSprint(sprintSummary.sprint, sprintIssues)
    : {
        id: 'no-sprint',
        name: 'Sin sprint activo',
        goal: '',
        startDate: '',
        endDate: '',
        status: 'Planned',
        progress: 0,
        totalPoints: 0,
        completedPoints: 0,
      }
  const kpis = buildKPIs(sprintIssues, backlog, sprint)
  return {
    id: jiraProject.key.toLowerCase(),
    key: jiraProject.key,
    name: jiraProject.name,
    description: '',
    color: color.bg,
    colorHex: color.hex,
    status:
      sprint.progress >= 70
        ? 'active'
        : sprint.progress >= 40
          ? 'at-risk'
          : 'on-hold',
    team: '',
    teamSize: 0,
    velocity: sprint.completedPoints,
    completion: sprint.progress,
    issues: mockSprintIssues,
    backlog: mockBacklog,
    sprint,
    kpis,
  }
}

// ─── Burndown Calculation ─────────────────────────────────────────────────────

export interface BurndownPoint {
  day: number
  date: string
  label: string
  ideal: number
  actual: number | null // null = future day (no data yet)
}

/** Calculate burndown data from sprint + issues */
export function calculateBurndown(
  sprint: JiraSprint,
  issues: JiraIssue[],
): BurndownPoint[] {
  if (!sprint.startDate || !sprint.endDate) return []
  const start = new Date(sprint.startDate)
  const end = new Date(sprint.endDate)
  const today = new Date()
  today.setHours(23, 59, 59, 999)
  const totalDays = Math.ceil(
    (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24),
  )
  if (totalDays <= 0) return []
  const totalPoints = issues.reduce((s, i) => s + (i.storyPoints || 0), 0)
  if (totalPoints === 0) return []
  const points: BurndownPoint[] = []
  for (let d = 0; d <= totalDays; d++) {
    const currentDate = new Date(start)
    currentDate.setDate(start.getDate() + d)
    currentDate.setHours(23, 59, 59, 999)
    const isFuture = currentDate > today

    // Ideal: linear from totalPoints to 0
    const ideal = Math.round(totalPoints * (1 - d / totalDays))

    // Actual: totalPoints - points of issues resolved by this date
    let actual: number | null = null
    if (!isFuture) {
      const resolvedPoints = issues
        .filter((i) => {
          if (!i.resolutiondate) return false
          const resolved = new Date(i.resolutiondate)
          return resolved <= currentDate
        })
        .reduce((s, i) => s + (i.storyPoints || 0), 0)
      actual = totalPoints - resolvedPoints
    }

    // Day label: "Mar 10", "Mar 11", etc.
    const label = currentDate.toLocaleDateString('es-AR', {
      month: 'short',
      day: 'numeric',
    })
    const dateStr = currentDate.toISOString().split('T')[0]
    points.push({
      day: d,
      date: dateStr,
      label,
      ideal,
      actual,
    })
  }
  return points
}

// ─── Data transformers ────────────────────────────────────────────────────────

function transformIssue(raw: any): JiraIssue {
  const fields = raw.fields || {}
  const statusCat = fields.status?.statusCategory?.key || 'new'
  const key = raw.key || (raw.id ? `#${raw.id}` : 'unknown')
  const summary = fields.summary || raw.summary || ''
  return {
    key,
    id: raw.id || raw.key,
    summary: summary || key,
    status: fields.status?.name || raw.status || 'To Do',
    statusCategory:
      statusCat === 'done'
        ? 'done'
        : statusCat === 'indeterminate'
          ? 'indeterminate'
          : 'new',
    priority: fields.priority?.name || raw.priority || 'Medium',
    issuetype: fields.issuetype?.name || raw.issuetype || 'Task',
    assignee: fields.assignee?.displayName || raw.assignee || 'Sin asignar',
    assigneeAvatar: fields.assignee?.avatarUrls?.['24x24'],
    storyPoints:
      fields.customfield_10016 || fields.story_points || raw.storyPoints || 0,
    labels: fields.labels || raw.labels || [],
    created: fields.created || raw.created || '',
    updated: fields.updated || raw.updated || '',
    duedate: fields.duedate || raw.duedate,
    resolutiondate: fields.resolutiondate || raw.resolutiondate,
    epicKey: fields.epic?.key || fields.customfield_10014,
    epicName: fields.epic?.name || fields.customfield_10011,
    description: fields.description || raw.description,
  }
}

/** Check if an issue has real data (not just an ID from search_issues) */
function isIncompleteIssue(issue: JiraIssue): boolean {
  return (
    !issue.summary || issue.summary.startsWith('#') || issue.key.startsWith('#')
  )
}

/** Enrich ID-only issues by fetching full details with get-issue (batched) */
async function enrichIncompleteIssues(
  issues: JiraIssue[],
): Promise<JiraIssue[]> {
  const incomplete = issues.filter(isIncompleteIssue)
  if (incomplete.length === 0) return issues
  console.log(`[jiraData] Enriching ${incomplete.length} incomplete issues...`)

  // Batch: enrich up to 15 issues to avoid too many calls
  const toEnrich = incomplete.slice(0, 15)
  const enriched = new Map<string, JiraIssue>()

  // Fetch in parallel (max 5 concurrent)
  const batchSize = 5
  for (let i = 0; i < toEnrich.length; i += batchSize) {
    const batch = toEnrich.slice(i, i + batchSize)
    const results = await Promise.all(
      batch.map(async (issue) => {
        const issueId = issue.id || issue.key.replace('#', '')
        try {
          const result = await runTool('get-issue', {
            issue_key: issueId,
          })
          if (result.success && result.data) {
            const full = transformIssue(result.data)
            // Only use if we got real data back
            if (full.summary && !full.summary.startsWith('#')) {
              return {
                id: issue.id || issue.key,
                data: full,
              }
            }
          }
        } catch (e) {
          console.warn(`[jiraData] Failed to enrich issue ${issueId}:`, e)
        }
        return null
      }),
    )
    results.forEach((r) => {
      if (r) enriched.set(r.id, r.data)
    })
  }
  if (enriched.size === 0) return issues
  console.log(`[jiraData] Enriched ${enriched.size} issues successfully`)

  // Replace incomplete issues with enriched versions
  return issues.map((issue) => {
    const key = issue.id || issue.key
    return enriched.get(key) || issue
  })
}
function transformSprint(raw: any): JiraSprint {
  return {
    id: raw.id,
    name: raw.name || 'Sprint',
    state: raw.state || 'active',
    startDate: raw.startDate,
    endDate: raw.endDate,
    completeDate: raw.completeDate,
    goal: raw.goal || '',
  }
}

// ─── API Functions ────────────────────────────────────────────────────────────

function safeArray(data: any): any[] {
  if (Array.isArray(data)) return data
  if (data === null || data === undefined) return []
  if (typeof data === 'object') {
    // Try common wrapper keys
    for (const key of [
      'values',
      'issues',
      'results',
      'projects',
      'sprints',
      'boards',
    ]) {
      if (Array.isArray(data[key])) return data[key]
    }
    // Single object? Wrap it
    if (data.key || data.id || data.name) return [data]
  }
  return []
}

/** Fetch all Jira projects */
export async function fetchProjects(): Promise<JiraProject[]> {
  const result = await runTool('get-projects')
  if (!result.success) {
    console.error('[jiraData] fetchProjects failed:', result.error)
    throw new Error(result.error || 'Error fetching projects')
  }
  console.log(
    '[jiraData] Raw projects response:',
    JSON.stringify(result.data).slice(0, 500),
  )
  let projects: any[] = safeArray(result.data)
  if (projects.length === 0) {
    console.warn('[jiraData] No projects found in response')
    return []
  }
  console.log(
    `[jiraData] Parsed ${projects.length} projects:`,
    projects.map((p: any) => p.key || p.name),
  )
  return projects.map((p: any) => ({
    id: p.id || p.key,
    key: p.key,
    name: p.name,
    avatarUrl: p.avatarUrls?.['48x48'],
    projectTypeKey: p.projectTypeKey,
  }))
}

/** Fetch boards for a project */
export async function fetchBoards(projectKey?: string): Promise<JiraBoard[]> {
  const args: any = {}
  if (projectKey) args.projectKey = projectKey
  const result = await runTool('get-boards', args)
  if (!result.success) {
    console.error('[jiraData] fetchBoards failed:', result.error)
    return []
  }
  const boards = safeArray(result.data)
  return boards.map((b: any) => ({
    id: b.id,
    name: b.name,
    type: b.type,
    projectKey: b.location?.projectKey,
  }))
}

/** Fetch sprints for a board, optionally filtered by state */
export async function fetchSprints(
  boardId: number,
  state?: string,
): Promise<JiraSprint[]> {
  const args: any = {
    board_id: String(boardId),
  }
  if (state) args.state = state
  const result = await runTool('get-sprints', args)
  if (!result.success) {
    console.error('[jiraData] fetchSprints failed:', result.error)
    return []
  }
  const sprints = safeArray(result.data)
  return sprints.map(transformSprint)
}

/** Fetch the active sprint for a project (with fallback to future/closed) */
export async function fetchActiveSprint(projectKey: string): Promise<{
  sprint: JiraSprint
  boardId: number
} | null> {
  // Step 1: Get boards — filter for Scrum (Kanban doesn't support sprints)
  const allBoards = await fetchBoards(projectKey)
  const scrumBoards = allBoards.filter(
    (b) => b.type === 'scrum' || b.type === 'simple',
  )
  if (scrumBoards.length === 0) {
    console.warn(
      `[jiraData] No scrum boards found for ${projectKey} (${allBoards.length} total boards)`,
    )
    return null
  }
  const boardId = scrumBoards[0].id

  // Step 2: Try active sprints
  let sprints = await fetchSprints(boardId, 'active')
  if (sprints.length > 0) {
    return {
      sprint: sprints[0],
      boardId,
    }
  }

  // Step 3: Fallback to future
  sprints = await fetchSprints(boardId, 'future')
  if (sprints.length > 0) {
    return {
      sprint: sprints[0],
      boardId,
    }
  }

  // Step 4: Fallback to most recent closed
  sprints = await fetchSprints(boardId, 'closed')
  if (sprints.length > 0) {
    // Return the most recent closed sprint
    return {
      sprint: sprints[sprints.length - 1],
      boardId,
    }
  }
  return null
}

/** Fetch issues for a sprint (requires boardId for MCP agile API) */
export async function fetchSprintIssues(
  sprintId: number,
  boardId?: number,
): Promise<JiraIssue[]> {
  // Try agile endpoint first (requires board_id + sprint_id)
  if (boardId) {
    const result = await runTool('get-sprint-issues', {
      board_id: String(boardId),
      sprint_id: String(sprintId),
    })
    if (result.success) {
      const issues = safeArray(result.data)
      if (issues.length > 0) {
        const transformed = issues.map(transformIssue)
        // Enrich ID-only issues (agile endpoint sometimes returns id without key)
        if (transformed.some(isIncompleteIssue)) {
          return enrichIncompleteIssues(transformed)
        }
        return transformed
      }
    }
  }

  // Fallback: JQL search (may return ID-only issues)
  const { issues } = await searchIssues(
    `sprint = ${sprintId} ORDER BY priority DESC, created DESC`,
    50,
  )

  // Enrich ID-only issues with full details
  if (issues.length > 0 && issues.some(isIncompleteIssue)) {
    return enrichIncompleteIssues(issues)
  }
  return issues
}

/** Search issues with JQL */
export async function searchIssues(
  jql: string,
  maxResults: number = 50,
): Promise<{
  issues: JiraIssue[]
  total: number
}> {
  const result = await runTool('search-issues', {
    jql,
    max_results: maxResults,
  })
  if (!result.success) {
    console.error('[jiraData] searchIssues failed:', result.error)
    return {
      issues: [],
      total: 0,
    }
  }
  const issues = safeArray(result.data)
  const total = result.data?.total || issues.length
  return {
    issues: issues.map(transformIssue),
    total,
  }
}

/** Fetch backlog issues (not in any sprint) */
export async function fetchBacklog(projectKey: string): Promise<JiraIssue[]> {
  // First try: issues not in any sprint
  const { issues: backlogIssues } = await searchIssues(
    `project = ${projectKey} AND sprint is EMPTY AND resolution = Unresolved ORDER BY priority DESC, created DESC`,
    50,
  )

  // If backlog is very small, also fetch ALL unresolved issues as fallback
  if (backlogIssues.length < 3) {
    const { issues: allIssues } = await searchIssues(
      `project = ${projectKey} AND resolution = Unresolved ORDER BY priority DESC, created DESC`,
      50,
    )
    // Merge: backlog first, then any additional issues not already included
    const seen = new Set(backlogIssues.map((i) => i.key))
    const merged = [...backlogIssues]
    for (const issue of allIssues) {
      if (!seen.has(issue.key)) {
        seen.add(issue.key)
        merged.push(issue)
      }
    }
    return merged
  }
  return backlogIssues
}

/** Fetch recent activity (recently updated issues) */
export async function fetchRecentActivity(
  projectKey: string,
  limit: number = 10,
): Promise<JiraIssue[]> {
  const { issues } = await searchIssues(
    `project = ${projectKey} ORDER BY updated DESC`,
    limit,
  )
  return issues
}

/** Build a full sprint summary with calculated metrics */
export async function fetchSprintSummary(
  projectKey: string,
): Promise<SprintSummary | null> {
  const sprintData = await fetchActiveSprint(projectKey)
  if (!sprintData) return null
  const issues = await fetchSprintIssues(
    sprintData.sprint.id,
    sprintData.boardId,
  )
  const totalPoints = issues.reduce((s, i) => s + (i.storyPoints || 0), 0)
  const completedPoints = issues
    .filter((i) => i.statusCategory === 'done')
    .reduce((s, i) => s + (i.storyPoints || 0), 0)
  const byStatus: Record<string, number> = {}
  issues.forEach((i) => {
    byStatus[i.status] = (byStatus[i.status] || 0) + 1
  })
  return {
    sprint: sprintData.sprint,
    issues,
    totalPoints,
    completedPoints,
    progress:
      totalPoints > 0 ? Math.round((completedPoints / totalPoints) * 100) : 0,
    byStatus,
  }
}

/** Fetch full project dashboard data */
export async function fetchProjectDashboard(
  projectKey: string,
): Promise<ProjectDashboard | null> {
  try {
    // Parallel fetch for speed
    const [boards, sprintSummary, backlog, recentActivity, allIssuesResult] =
      await Promise.all([
        fetchBoards(projectKey),
        fetchSprintSummary(projectKey),
        fetchBacklog(projectKey),
        fetchRecentActivity(projectKey, 10),
        searchIssues(`project = ${projectKey} AND resolution = Unresolved`, 1), // Just for total count
      ])

    // Calculate KPIs from sprint issues + backlog
    const sprintIssues = sprintSummary?.issues || []
    const allIssues = [...sprintIssues, ...backlog]
    const openIssues = allIssues.filter(
      (i) => i.statusCategory === 'new',
    ).length
    const inProgress = allIssues.filter(
      (i) => i.statusCategory === 'indeterminate',
    ).length
    const done = sprintIssues.filter((i) => i.statusCategory === 'done').length
    const blockers = allIssues.filter(
      (i) =>
        i.priority.toLowerCase().includes('high') ||
        i.priority.toLowerCase().includes('highest') ||
        i.priority.toLowerCase().includes('blocker'),
    ).length
    return {
      project: {
        id: projectKey,
        key: projectKey,
        name: projectKey, // Will be enriched by fetchProjects
      },
      boards,
      activeSprint: sprintSummary,
      recentIssues: recentActivity,
      backlog,
      kpis: {
        totalIssues: allIssuesResult.total,
        openIssues,
        inProgress,
        done,
        velocity: sprintSummary?.completedPoints || 0,
        blockers,
      },
    }
  } catch (err) {
    console.error('[jiraData] fetchProjectDashboard failed:', err)
    return null
  }
}
